import { useState, useEffect, useCallback } from 'react';
import {
  Bot, ToggleLeft, ToggleRight, Loader2, AlertCircle, Search,
  Brain, FileText, Mail, BarChart3, Lightbulb, Phone, MapPin,
  Zap, ShieldCheck, Package, ShoppingCart, Webhook, Filter, Sparkles, Activity,
} from 'lucide-react';
import { api } from '@/utils/api';

interface UsageEntry {
  feature: string;
  _count: number;
  _sum: { creditsUsed: number | null };
}

interface Agent {
  key: string;
  name: string;
  description: string;
  credits: number;
  category: string;
  defaultEnabled: boolean;
  enabled: boolean;
}

const CATEGORY_ICONS: Record<string, any> = {
  CRM: Brain,
  Sales: FileText,
  Analytics: BarChart3,
  'AURA Sales App': Phone,
  GST: ShieldCheck,
  Leads: Zap,
  Warehouse: Package,
  Ecosystem: Webhook,
};

const CATEGORY_COLORS: Record<string, string> = {
  CRM: 'bg-blue-100 text-blue-700',
  Sales: 'bg-green-100 text-green-700',
  Analytics: 'bg-purple-100 text-purple-700',
  'AURA Sales App': 'bg-orange-100 text-orange-700',
  GST: 'bg-red-100 text-red-700',
  Leads: 'bg-yellow-100 text-yellow-700',
  Warehouse: 'bg-teal-100 text-teal-700',
  Ecosystem: 'bg-indigo-100 text-indigo-700',
};

export function AIAgentsPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [usage, setUsage] = useState<UsageEntry[]>([]);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const [agentRes, usageRes] = await Promise.all([
        api.get('/ai/agents'),
        api.get('/credits/usage').catch(() => ({ data: { data: { features: [] } } })),
      ]);
      setAgents(agentRes.data.data);
      setUsage(usageRes.data.data?.features || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const toggleAgent = async (key: string, enabled: boolean) => {
    setSaving(key);
    try {
      const res = await api.put('/ai/agents', { toggles: { [key]: enabled } });
      setAgents(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  const categories = ['all', ...Array.from(new Set(agents.map(a => a.category)))];

  const filtered = agents.filter(a => {
    if (filterCategory !== 'all' && a.category !== filterCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q) || a.key.toLowerCase().includes(q);
    }
    return true;
  });

  const creditAgents = filtered.filter(a => a.credits > 0);
  const automationAgents = filtered.filter(a => a.credits === 0);

  const enabledCount = agents.filter(a => a.enabled).length;
  const totalCreditsIfAllRun = agents.filter(a => a.enabled && a.credits > 0).reduce((s, a) => s + a.credits, 0);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
            <Bot size={28} className="text-[var(--ink-0)]" />
            AI Agents
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage all AI-powered features and automation agents. Toggle on/off per your needs.
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-2">
            <span className="font-semibold text-green-700">{enabledCount}</span>
            <span className="text-green-600"> / {agents.length} active</span>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-2">
            <span className="font-semibold text-blue-700">{totalCreditsIfAllRun}</span>
            <span className="text-blue-600"> credits/use (AI agents)</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search agents..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <div className="flex gap-1">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setFilterCategory(c)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filterCategory === c
                  ? 'bg-[var(--ink-0)] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* AI-Powered Agents (credit-based) */}
          {creditAgents.length > 0 && (
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Sparkles size={20} className="text-amber-500" />
                AI-Powered Agents
                <span className="text-xs font-normal text-gray-500">(uses AI credits per call)</span>
              </h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {creditAgents.map(agent => (
                  <AgentCard key={agent.key} agent={agent} saving={saving} usage={usage} onToggle={toggleAgent} />
                ))}
              </div>
            </div>
          )}

          {/* Automation Agents (no credits) */}
          {automationAgents.length > 0 && (
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Zap size={20} className="text-blue-500" />
                Automation Agents
                <span className="text-xs font-normal text-gray-500">(rule-based, no AI credits)</span>
              </h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {automationAgents.map(agent => (
                  <AgentCard key={agent.key} agent={agent} saving={saving} usage={usage} onToggle={toggleAgent} />
                ))}
              </div>
            </div>
          )}

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Filter size={40} className="mb-3 text-gray-300" />
              <p className="text-sm">No agents match your search</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent, saving, usage, onToggle }: { agent: Agent; saving: string | null; usage: UsageEntry[]; onToggle: (key: string, enabled: boolean) => void }) {
  const Icon = CATEGORY_ICONS[agent.category] || Bot;
  const catColor = CATEGORY_COLORS[agent.category] || 'bg-gray-100 text-gray-700';
  const isSaving = saving === agent.key;

  const usageEntry = usage.find(u => u.feature === agent.key);
  const usageCount = usageEntry ? (usageEntry as any)._count : 0;
  const creditsSpent = usageEntry ? Number((usageEntry as any)._sum?.creditsUsed || 0) : 0;

  return (
    <div className={`rounded-xl border p-4 transition-all ${agent.enabled ? 'border-green-200 bg-white shadow-sm' : 'border-gray-200 bg-gray-50 opacity-75'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`mt-0.5 rounded-lg p-2 ${agent.enabled ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'bg-gray-100 text-gray-400'}`}>
            <Icon size={20} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{agent.name}</h3>
            <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{agent.description}</p>
          </div>
        </div>
        <button
          onClick={() => onToggle(agent.key, !agent.enabled)}
          disabled={isSaving}
          className="flex-shrink-0"
        >
          {isSaving ? (
            <Loader2 size={28} className="animate-spin text-gray-400" />
          ) : agent.enabled ? (
            <ToggleRight size={28} className="text-green-500 hover:text-green-600 transition-colors" />
          ) : (
            <ToggleLeft size={28} className="text-gray-400 hover:text-gray-500 transition-colors" />
          )}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${catColor}`}>
          {agent.category}
        </span>
        {agent.credits > 0 && (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
            {agent.credits} credit{agent.credits > 1 ? 's' : ''}/use
          </span>
        )}
        {agent.credits === 0 && (
          <span className="rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-500">
            Free
          </span>
        )}
        {usageCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
            <Activity size={10} />
            {usageCount} calls{creditsSpent > 0 ? ` · ${creditsSpent} cr` : ''}
          </span>
        )}
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${agent.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {agent.enabled ? 'Active' : 'Disabled'}
        </span>
      </div>
    </div>
  );
}
